"use client"

import { useMemo } from "react"
import { usePathname, useRouter, useSearchParams } from "next/navigation"

type FilterLabelOption = {
  value: string
  label: string
}

type ActiveFiltersProps = {
  brandLabels?: FilterLabelOption[]
  categoryLabels?: FilterLabelOption[]
  collectionLabels?: FilterLabelOption[]
}

const availabilityLabels: Record<string, string> = {
  standard: "Disponible",
  preorder: "Preorder",
  custom: "Custom",
}

const normalizeListParam = (params: URLSearchParams, key: string) => {
  const all = params.getAll(key)
  if (all.length) return all
  const raw = params.get(key)
  if (!raw) return []
  return raw
    .split(",")
    .map((value) => value.trim())
    .filter(Boolean)
}

const humanizeValue = (value: string) => {
  const cleaned = value.trim()
  if (!cleaned) return value
  const spaced = cleaned.replace(/[-_]+/g, " ")
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

const buildLabelMap = (entries?: FilterLabelOption[]) => {
  const map = new Map<string, string>()
  ;(entries || []).forEach((entry) => {
    const value = String(entry.value || "").trim()
    const label = String(entry.label || "").trim()
    if (!value || !label) return
    map.set(value, label)
  })
  return map
}

export default function ActiveFilters({
  brandLabels,
  categoryLabels,
  collectionLabels,
}: ActiveFiltersProps) {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const chips = useMemo(() => {
    const params = new URLSearchParams(searchParams)
    const items: Array<{ key: string; label: string; remove: () => void }> = []

    const brandMap = buildLabelMap(brandLabels)
    const categoryMap = buildLabelMap(categoryLabels)
    const collectionMap = buildLabelMap(collectionLabels)

    const pushChip = (key: string, value: string, label?: string) => {
      items.push({
        key: `${key}:${value}`,
        label: label || humanizeValue(value),
        remove: () => {
          const next = new URLSearchParams(params)
          const values = normalizeListParam(next, key).filter(
            (entry) => entry !== value
          )
          next.delete(key)
          values.forEach((entry) => next.append(key, entry))
          next.delete("page")
          next.delete("cursor")
          const query = next.toString()
          router.push(query ? `${pathname}?${query}` : pathname)
        },
      })
    }

    normalizeListParam(params, "brand").forEach((value) =>
      pushChip("brand", value, brandMap.get(value) || humanizeValue(value))
    )
    normalizeListParam(params, "category").forEach((value) =>
      pushChip(
        "category",
        value,
        categoryMap.get(value) || humanizeValue(value)
      )
    )
    normalizeListParam(params, "collection").forEach((value) =>
      pushChip(
        "collection",
        value,
        collectionMap.get(value) || humanizeValue(value)
      )
    )

    normalizeListParam(params, "color").forEach((value) =>
      pushChip("color", value, `Color: ${humanizeValue(value)}`)
    )
    normalizeListParam(params, "size").forEach((value) =>
      pushChip("size", value, `Talle: ${humanizeValue(value)}`)
    )
    normalizeListParam(params, "material").forEach((value) =>
      pushChip("material", value, `Material: ${humanizeValue(value)}`)
    )

    normalizeListParam(params, "availabilityType").forEach((value) => {
      pushChip(
        "availabilityType",
        value,
        availabilityLabels[value] || humanizeValue(value)
      )
    })

    normalizeListParam(params, "currency").forEach((value) =>
      pushChip("currency", value, `Moneda: ${value.toUpperCase()}`)
    )

    if (params.get("inStock") === "true") {
      items.push({
        key: "inStock",
        label: "En stock",
        remove: () => {
          const next = new URLSearchParams(params)
          next.delete("inStock")
          next.delete("page")
          next.delete("cursor")
          const query = next.toString()
          router.push(query ? `${pathname}?${query}` : pathname)
        },
      })
    }

    if (params.get("discounted") === "true") {
      items.push({
        key: "discounted",
        label: "En oferta",
        remove: () => {
          const next = new URLSearchParams(params)
          next.delete("discounted")
          next.delete("page")
          next.delete("cursor")
          const query = next.toString()
          router.push(query ? `${pathname}?${query}` : pathname)
        },
      })
    }

    const priceMin = params.get("priceMin") || params.get("minPrice")
    const priceMax = params.get("priceMax") || params.get("maxPrice")
    if (priceMin || priceMax) {
      items.push({
        key: "price",
        label: `Precio ${priceMin || "0"} - ${priceMax || "∞"}`,
        remove: () => {
          const next = new URLSearchParams(params)
          next.delete("priceMin")
          next.delete("priceMax")
          next.delete("minPrice")
          next.delete("maxPrice")
          next.delete("page")
          next.delete("cursor")
          const query = next.toString()
          router.push(query ? `${pathname}?${query}` : pathname)
        },
      })
    }

    return items
  }, [
    brandLabels,
    categoryLabels,
    collectionLabels,
    pathname,
    router,
    searchParams,
  ])

  const clearAll = () => {
    const next = new URLSearchParams(searchParams)
    ;[
      "brand",
      "category",
      "collection",
      "color",
      "size",
      "material",
      "availabilityType",
      "currency",
      "inStock",
      "discounted",
      "priceMin",
      "priceMax",
      "minPrice",
      "maxPrice",
    ].forEach((key) => next.delete(key))
    next.delete("page")
    next.delete("cursor")
    const query = next.toString()
    router.push(query ? `${pathname}?${query}` : pathname)
  }

  if (!chips.length) return null

  return (
    <div className="mb-5 flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-black/10 pb-4">
      {chips.map((chip) => (
        <button
          key={chip.key}
          type="button"
          onClick={chip.remove}
          className="font-lealti-light text-[11px] uppercase tracking-[0.15em] text-black/55 transition hover:text-black"
        >
          {chip.label} ×
        </button>
      ))}
      <button
        type="button"
        onClick={clearAll}
        className="font-lealti-light text-[10px] uppercase tracking-[0.2em] text-black/30 transition hover:text-black"
      >
        Limpiar
      </button>
    </div>
  )
}
